import { useAppDispatch } from 'app/hooks';
import store from 'app/store';
import clsx from 'clsx';
import { useContextMenu } from 'components/common/context-menu-manager';
import Tooltip from 'components/layout/tooltip';
import LineSetPoint from 'components/structure-page/2d/lineset-point';
import { STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  ContextMenuItemType,
  LevelType,
  LineSetPointType,
  LineSetType
} from 'data/types';
import { setConfigurations } from 'features/configurations';
import { useLinesetActions } from 'hooks/use-lineset-actions';
import { produce } from 'immer';
import { nanoid } from 'nanoid';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { beginLinesetDrag, endLinesetDrag } from 'utils/lineset-drag-state';
import {
  linesetPerimetersByLevel,
  perimetersForLevel
} from 'utils/lineset-level-perimeters';
import {
  buildCrossLevelPointInsertions,
  getSegmentLevel,
  isLevelTransitionPoint,
  materializeLinesetPointLevels,
  resolveLinesetPointLevels,
  resolveSegmentLevelForPointInsertion
} from 'utils/lineset-point-levels';
import {
  snapLinesetPoint,
  snapLinesetPointToSegment
} from 'utils/lineset-snap';
import DndMonitor, { DndMonitorListenersType } from './dnd-monitor';
import {
  STRUCTURE_GRID_LINESET_LINES_Z_CLASS,
  STRUCTURE_GRID_LINESET_POINTS_Z_CLASS,
  STRUCTURE_GRID_LINESET_TRANSITIONS_Z_CLASS
} from './structure-grid-stacking';

const LINESET_LAYER_CLASS =
  'pointer-events-none absolute inset-0 h-full w-full overflow-visible';

type GridLineSetProps = {
  configurationId: string;
  lineset: LineSetType;
  currentLevelUniqueId?: string;
  structure?: LevelType[];
  zoom?: number;
  readOnly?: boolean;
};

export default function GridLineSet({
  lineset,
  configurationId,
  currentLevelUniqueId,
  structure = [],
  zoom = 1,
  readOnly = false
}: GridLineSetProps) {
  const safeZoom = zoom > 0 ? zoom : 1;
  const dispatch = useAppDispatch();
  const { deletePoint, deleteLineset, movePoint } = useLinesetActions(
    configurationId,
    lineset.uniqueId
  );
  const { openContextMenu } = useContextMenu();
  const [hoveredSegmentIndex, setHoveredSegmentIndex] = useState<number | null>(
    null
  );
  const [pointTransforms, setPointTransforms] = useState<
    Map<string, { positionX: number; positionY: number }>
  >(new Map());
  const pointTransformsRef = useRef(pointTransforms);

  useEffect(() => {
    pointTransformsRef.current = pointTransforms;
  }, [pointTransforms]);

  const perimetersByLevel = useMemo(
    () => linesetPerimetersByLevel(structure),
    [structure]
  );

  const pointPerimeters = useCallback(
    (pointUniqueId: string) => {
      const pointIndex = lineset.points.findIndex(
        (p) => p.uniqueId === pointUniqueId
      );
      const levelUniqueId =
        resolveLinesetPointLevels(lineset)[pointIndex] ?? currentLevelUniqueId;
      return perimetersForLevel(perimetersByLevel, levelUniqueId);
    },
    [lineset, currentLevelUniqueId, perimetersByLevel]
  );

  const snappedDragPosition = useCallback(
    (
      point: LineSetPointType,
      pointUniqueId: string,
      rawTransform: { x: number; y: number }
    ) =>
      snapLinesetPoint(
        {
          x: point.positionX + rawTransform.x / STRUCTURE_GRID_SIZE,
          y: point.positionY + rawTransform.y / STRUCTURE_GRID_SIZE
        },
        pointPerimeters(pointUniqueId)
      ),
    [pointPerimeters]
  );

  const dragListeners: DndMonitorListenersType = {
    onDragStart: (event) => {
      const { active } = event;
      if (active.data.current?.type === 'grid-lineset-point') {
        document.body.style.cursor = 'grabbing';
        beginLinesetDrag(String(active.id));
      }
    },
    onDragMove: (event) => {
      const { active } = event;
      if (active.data.current?.type === 'grid-lineset-point') {
        const dragLinesetId = active.data.current.linesetUniqueId as string;
        if (dragLinesetId !== lineset.uniqueId) return;

        const pointUniqueId = active.data.current.pointUniqueId as string;
        const point = lineset.points.find((p) => p.uniqueId === pointUniqueId);

        if (!point) return;

        const initialRect = active.rect.current.initial;
        const translatedRect = active.rect.current.translated;

        if (initialRect && translatedRect) {
          const rawTransform = {
            x: (translatedRect.left - initialRect.left) / safeZoom,
            y: (translatedRect.top - initialRect.top) / safeZoom
          };

          const snapped = snappedDragPosition(
            point,
            pointUniqueId,
            rawTransform
          );
          const snappedTransform = {
            positionX: (snapped.x - point.positionX) * STRUCTURE_GRID_SIZE,
            positionY: (snapped.y - point.positionY) * STRUCTURE_GRID_SIZE
          };

          setPointTransforms((prev) => {
            const next = new Map(prev);
            next.set(pointUniqueId, snappedTransform);
            return next;
          });
        }
      }
    },
    onDragEnd: (event) => {
      const { active } = event;
      if (active.data.current?.type === 'grid-lineset-point') {
        document.body.style.cursor = '';
        endLinesetDrag(String(active.id));
        const dragLinesetId = active.data.current.linesetUniqueId as string;
        if (dragLinesetId !== lineset.uniqueId) return;

        const pointUniqueId = active.data.current.pointUniqueId as string;
        const point = lineset.points.find((p) => p.uniqueId === pointUniqueId);
        if (point) {
          const transform = pointTransformsRef.current.get(pointUniqueId);
          if (transform) {
            const snapped = snappedDragPosition(point, pointUniqueId, {
              x: transform.positionX,
              y: transform.positionY
            });

            movePoint(pointUniqueId, {
              positionX: snapped.x,
              positionY: snapped.y
            });
          }

          setPointTransforms((prev) => {
            const next = new Map(prev);
            next.delete(pointUniqueId);
            return next;
          });
        }
      }
    },
    onDragCancel: (event) => {
      const { active } = event;
      if (active.data.current?.type === 'grid-lineset-point') {
        document.body.style.cursor = '';
        endLinesetDrag(String(active.id));
        const dragLinesetId = active.data.current.linesetUniqueId as string;
        if (dragLinesetId !== lineset.uniqueId) return;

        const pointUniqueId = active.data.current.pointUniqueId as string;
        setPointTransforms((prev) => {
          if (!prev.has(pointUniqueId)) return prev;
          const next = new Map(prev);
          next.delete(pointUniqueId);
          return next;
        });
      }
    }
  };

  const getPointPosition = useCallback(
    (point: LineSetPointType) => {
      const baseX = point.positionX * STRUCTURE_GRID_SIZE;
      const baseY = point.positionY * STRUCTURE_GRID_SIZE;
      const pointUniqueId =
        point.uniqueId || `point-${lineset.points.indexOf(point)}`;
      const transform = pointTransforms.get(pointUniqueId);

      if (transform) {
        return {
          positionX: baseX + transform.positionX,
          positionY: baseY + transform.positionY
        };
      }

      return {
        positionX: baseX,
        positionY: baseY
      };
    },
    [pointTransforms, lineset.points]
  );

  const handleLineClick = useCallback(
    (
      segmentIndex: number,
      x: number,
      y: number,
      targetLevelUniqueId?: string
    ) => {
      const updatedConfigurations = produce(
        store.getState().configurations.configurations,
        (draft) => {
          const config = draft.find((c) => c.uniqueId === configurationId);

          if (!config || !config.lineSets) {
            return;
          }

          const linesetIndex = config.lineSets.findIndex(
            (ls) => ls.uniqueId === lineset.uniqueId
          );

          if (linesetIndex === -1) {
            return;
          }

          const draftLineset = config.lineSets[linesetIndex];
          const segmentStart = draftLineset.points[segmentIndex];
          const segmentEnd = draftLineset.points[segmentIndex + 1];
          if (!segmentStart || !segmentEnd) {
            return;
          }

          const snappedPoint = snapLinesetPointToSegment(
            { x, y },
            { x: segmentStart.positionX, y: segmentStart.positionY },
            { x: segmentEnd.positionX, y: segmentEnd.positionY },
            perimetersForLevel(
              perimetersByLevel,
              targetLevelUniqueId ??
                resolveSegmentLevelForPointInsertion(
                  draftLineset,
                  segmentIndex
                ) ??
                currentLevelUniqueId
            )
          );

          const makeLinesetPoint = (
            levelUniqueId: string | undefined
          ): LineSetPointType => ({
            positionX: snappedPoint.x,
            positionY: snappedPoint.y,
            uniqueId: nanoid(5),
            lineSetUniqueId: lineset.uniqueId,
            indexNumber: 0,
            ...(levelUniqueId ? { levelUniqueId } : {})
          });

          if (targetLevelUniqueId) {
            const pointLevels = resolveLinesetPointLevels(draftLineset);
            materializeLinesetPointLevels(draftLineset);

            const insertions = buildCrossLevelPointInsertions(
              pointLevels,
              segmentIndex,
              snappedPoint,
              targetLevelUniqueId
            );
            draftLineset.points.splice(
              segmentIndex + 1,
              0,
              ...insertions.map((insertion) =>
                makeLinesetPoint(insertion.levelUniqueId)
              )
            );
            return;
          }

          const segmentLevelUniqueId = resolveSegmentLevelForPointInsertion(
            draftLineset,
            segmentIndex
          );

          draftLineset.points.splice(
            segmentIndex + 1,
            0,
            makeLinesetPoint(segmentLevelUniqueId)
          );
        }
      );
      dispatch(setConfigurations(updatedConfigurations));
    },
    [
      lineset.uniqueId,
      configurationId,
      dispatch,
      perimetersByLevel,
      currentLevelUniqueId
    ]
  );

  const handleDeletePoint = useCallback(
    (pointUniqueId: string) => {
      if (lineset.points.length <= 2) {
        return;
      }
      deletePoint(pointUniqueId);
    },
    [lineset.points.length, deletePoint]
  );

  const handleDeleteLineset = useCallback(() => {
    deleteLineset();
  }, [deleteLineset]);

  const handleLineContextMenu = useCallback(
    (
      e: React.MouseEvent<SVGLineElement>,
      segmentKey: string,
      segmentIndex: number
    ) => {
      e.preventDefault();
      e.stopPropagation();
      const rect = (e.currentTarget as SVGLineElement)
        .closest('svg')
        ?.getBoundingClientRect();
      const clickX = rect
        ? (e.clientX - rect.left) / (STRUCTURE_GRID_SIZE * safeZoom)
        : undefined;
      const clickY = rect
        ? (e.clientY - rect.top) / (STRUCTURE_GRID_SIZE * safeZoom)
        : undefined;
      const menuItems: ContextMenuItemType[] = [];
      if (
        clickX !== undefined &&
        clickY !== undefined &&
        typeof handleLineClick === 'function' &&
        segmentIndex >= 0
      ) {
        menuItems.push({
          label: 'Add Point',
          onClick: () => handleLineClick(segmentIndex, clickX, clickY)
        });

        const currentLevelIndex = currentLevelUniqueId
          ? structure.findIndex((l) => l.uniqueId === currentLevelUniqueId)
          : -1;
        const levelAbove =
          currentLevelIndex !== -1
            ? structure[currentLevelIndex + 1]
            : undefined;
        const levelBelow =
          currentLevelIndex > 0 ? structure[currentLevelIndex - 1] : undefined;

        if (levelAbove) {
          menuItems.push({
            label: `Add Point Above (${levelAbove.label})`,
            onClick: () =>
              handleLineClick(segmentIndex, clickX, clickY, levelAbove.uniqueId)
          });
        }
        if (levelBelow) {
          menuItems.push({
            label: `Add Point Below (${levelBelow.label})`,
            onClick: () =>
              handleLineClick(segmentIndex, clickX, clickY, levelBelow.uniqueId)
          });
        }
      }
      menuItems.push({
        label: 'Delete Line Set',
        onClick: () => handleDeleteLineset()
      });
      openContextMenu(e.clientX, e.clientY, menuItems, segmentKey);
    },
    [
      handleDeleteLineset,
      handleLineClick,
      openContextMenu,
      safeZoom,
      structure,
      currentLevelUniqueId
    ]
  );

  if (lineset.points.length < 2) {
    return null;
  }

  const pointLevels = resolveLinesetPointLevels(lineset);
  const isOnRelevantFloor = pointLevels.includes(currentLevelUniqueId);

  if (!isOnRelevantFloor) {
    return null;
  }

  const getLevelIndexById = (levelUniqueId: string | undefined): number => {
    if (!levelUniqueId || structure.length === 0) return -1;
    return structure.findIndex((l) => l.uniqueId === levelUniqueId);
  };

  const transitionIndicators = lineset.points.flatMap((point, index) => {
    if (!isLevelTransitionPoint(pointLevels, index)) return [];
    const fromLevelUniqueId = pointLevels[index];
    const toLevelUniqueId = pointLevels[index + 1];
    if (
      currentLevelUniqueId !== fromLevelUniqueId &&
      currentLevelUniqueId !== toLevelUniqueId
    ) {
      return [];
    }
    const otherLevelUniqueId =
      currentLevelUniqueId === fromLevelUniqueId
        ? toLevelUniqueId
        : fromLevelUniqueId;
    const currentLevelIndex = getLevelIndexById(currentLevelUniqueId);
    const otherLevelIndex = getLevelIndexById(otherLevelUniqueId);
    const direction: 'up' | 'down' =
      currentLevelIndex !== -1 &&
      otherLevelIndex !== -1 &&
      otherLevelIndex > currentLevelIndex
        ? 'up'
        : 'down';
    const targetLevelLabel =
      structure.find((l) => l.uniqueId === otherLevelUniqueId)?.label ||
      'another floor';
    return [{ point, pointIndex: index, direction, targetLevelLabel }];
  });

  return (
    <>
      {!readOnly && <DndMonitor listeners={dragListeners} />}
      <svg
        className={clsx(
          LINESET_LAYER_CLASS,
          STRUCTURE_GRID_LINESET_LINES_Z_CLASS
        )}
        style={{ width: '100%', height: '100%', overflow: 'visible' }}
      >
        <g className='lineset-ghost-group pointer-events-none'>
          {lineset.points.map((point, index) => {
            if (index === lineset.points.length - 1) return null;

            if (getSegmentLevel(pointLevels, index) === currentLevelUniqueId) {
              return null;
            }

            const nextPoint = lineset.points[index + 1];
            const point1Pos = getPointPosition(point);
            const point2Pos = getPointPosition(nextPoint);

            const segmentKey = `ghost-segment-${lineset.uniqueId}-${point.positionX}-${point.positionY}-${nextPoint.positionX}-${nextPoint.positionY}`;
            return (
              <line
                key={segmentKey}
                x1={point1Pos.positionX}
                y1={point1Pos.positionY}
                x2={point2Pos.positionX}
                y2={point2Pos.positionY}
                stroke='#000000'
                strokeWidth='3'
                strokeOpacity={0.25}
                className='pointer-events-none'
                data-lineset-ghost-line
              />
            );
          })}
        </g>
        <g className='lineset-group pointer-events-auto'>
          {lineset.points.map((point, index) => {
            if (index === lineset.points.length - 1) return null;

            if (getSegmentLevel(pointLevels, index) !== currentLevelUniqueId) {
              return null;
            }

            const nextPoint = lineset.points[index + 1];
            const point1Pos = getPointPosition(point);
            const point2Pos = getPointPosition(nextPoint);
            const x1 = point1Pos.positionX;
            const y1 = point1Pos.positionY;
            const x2 = point2Pos.positionX;
            const y2 = point2Pos.positionY;

            const segmentKey = `segment-${lineset.uniqueId}-${point.positionX}-${point.positionY}-${nextPoint.positionX}-${nextPoint.positionY}`;
            return (
              <g key={segmentKey}>
                <line
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke='#000000'
                  strokeWidth='3'
                  className={readOnly ? 'cursor-default' : 'cursor-pointer'}
                  data-lineset-line
                  onMouseEnter={() => setHoveredSegmentIndex(index)}
                  onMouseLeave={() => setHoveredSegmentIndex(null)}
                  onClick={
                    readOnly
                      ? undefined
                      : (e) => {
                          const rect = (e.currentTarget as SVGLineElement)
                            .closest('svg')
                            ?.getBoundingClientRect();
                          if (!rect) return;
                          const clickX =
                            (e.clientX - rect.left) /
                            (STRUCTURE_GRID_SIZE * safeZoom);
                          const clickY =
                            (e.clientY - rect.top) /
                            (STRUCTURE_GRID_SIZE * safeZoom);
                          handleLineClick(index, clickX, clickY);
                        }
                  }
                  onContextMenu={
                    readOnly
                      ? undefined
                      : (e) => handleLineContextMenu(e, segmentKey, index)
                  }
                />
                {hoveredSegmentIndex === index && (
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke='#666666'
                    strokeWidth='8'
                    className='pointer-events-none opacity-30'
                  />
                )}
              </g>
            );
          })}
        </g>
      </svg>
      <svg
        className={clsx(
          LINESET_LAYER_CLASS,
          STRUCTURE_GRID_LINESET_POINTS_Z_CLASS
        )}
        style={{ width: '100%', height: '100%', overflow: 'visible' }}
      >
        <g className='lineset-group pointer-events-auto'>
          {lineset.points.map((point, index) => {
            if (isLevelTransitionPoint(pointLevels, index)) {
              return null;
            }
            if (pointLevels[index] !== currentLevelUniqueId) {
              return null;
            }

            const pointUniqueId = point.uniqueId || `point-${index}`;
            const isConnectionAnchor =
              index === 0 || index === lineset.points.length - 1;

            return (
              <g key={pointUniqueId}>
                <LineSetPoint
                  point={point}
                  pointIndex={index}
                  linesetUniqueId={lineset.uniqueId}
                  canDeletePoint={lineset.points.length > 2}
                  onDeletePoint={handleDeletePoint}
                  onDeleteLineset={handleDeleteLineset}
                  previewPosition={getPointPosition(point)}
                  readOnly={readOnly}
                  draggable={!isConnectionAnchor}
                />
              </g>
            );
          })}
        </g>
      </svg>
      {transitionIndicators.map((indicator) => {
        const pos = getPointPosition(indicator.point);
        const arrowSymbol = indicator.direction === 'up' ? '↑' : '↓';
        const directionText =
          indicator.direction === 'up' ? 'upward' : 'downward';
        const tooltipText = `Lineset continues ${directionText} to ${indicator.targetLevelLabel}`;
        const pointUniqueId =
          indicator.point.uniqueId || `point-${indicator.pointIndex}`;

        return (
          <svg
            key={`cross-floor-${pointUniqueId}`}
            className={clsx(
              LINESET_LAYER_CLASS,
              STRUCTURE_GRID_LINESET_TRANSITIONS_Z_CLASS
            )}
            style={{ width: '100%', height: '100%', overflow: 'visible' }}
          >
            <g className='pointer-events-auto'>
              <g className='cross-floor-indicator pointer-events-none'>
                <circle
                  cx={pos.positionX}
                  cy={pos.positionY}
                  r={10}
                  fill='#000000'
                />
                <text
                  x={pos.positionX}
                  y={pos.positionY + 5}
                  textAnchor='middle'
                  fontSize='14'
                  fontWeight='bold'
                  fill='white'
                >
                  {arrowSymbol}
                </text>
              </g>
              <LineSetPoint
                point={indicator.point}
                pointIndex={indicator.pointIndex}
                linesetUniqueId={lineset.uniqueId}
                canDeletePoint={lineset.points.length > 2}
                onDeletePoint={handleDeletePoint}
                onDeleteLineset={handleDeleteLineset}
                previewPosition={pos}
                radius={10}
                transparent
                readOnly={readOnly}
                draggable={
                  indicator.pointIndex !== 0 &&
                  indicator.pointIndex !== lineset.points.length - 1
                }
              />
              <foreignObject
                x={pos.positionX - 12}
                y={pos.positionY - 12}
                width={24}
                height={24}
                className='pointer-events-none overflow-visible'
              >
                <div className='relative h-full w-full'>
                  <Tooltip text={tooltipText} />
                </div>
              </foreignObject>
            </g>
          </svg>
        );
      })}
    </>
  );
}
